import React, { FC, useEffect } from 'react';
import * as Sentry from '@sentry/react';
import CoveringScreen from './CoveringScreen';

/** How long the fallback screen stays visible before the page reloads. */
export const ERROR_RELOAD_DELAY_MS = 10 * 1000;

interface FallbackProps {
  eventId: string;
  reload: () => void;
}

/**
 * Shown when a render error was caught. No hooks that need a provider: this
 * renders outside IntlProvider and QueryClientProvider, so the text is
 * hardcoded Swedish like the other startup screens.
 */
const ErrorFallback: FC<FallbackProps> = ({ eventId, reload }) => {
  useEffect(() => {
    const timeout = setTimeout(reload, ERROR_RELOAD_DELAY_MS);
    return () => clearTimeout(timeout);
  }, [reload]);

  // Same full-height wrapper as main.tsx: the fallback replaces the whole
  // tree, so nothing else gives the screen its height.
  return (
    <div className="h-screen bg-white">
      <CoveringScreen
        message={
          // Spans, not h2/p: the message is rendered inside the screen's h2.
          <span className="flex flex-col gap-4 text-gray-900">
            <span>Ett fel uppstod, skärmen startar om strax</span>
            <span className="text-center text-gray font-small">
              Ange denna kod vid kontakt med supporten: {eventId}
            </span>
          </span>
        }
      />
    </div>
  );
};

const reloadPage = (): void => window.location.reload();

interface Props {
  children: React.ReactNode;
  /** Injectable for tests; defaults to reloading the page. */
  reload?: () => void;
}

/**
 * Catches render errors anywhere below it, reports them to Sentry with the
 * component stack, shows a full-screen message with the event id and reloads
 * the page after ERROR_RELOAD_DELAY_MS. Without it React 18 leaves #root
 * empty, which index.html renders as a white page with a spinner until an
 * external watchdog restarts the app.
 */
const AppErrorBoundary: FC<Props> = ({ children, reload = reloadPage }) => {
  return (
    <Sentry.ErrorBoundary
      fallback={({ eventId }) => (
        <ErrorFallback eventId={eventId} reload={reload} />
      )}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
};

export default AppErrorBoundary;
